import { jsPDF } from 'jspdf';
import { format, parseISO, differenceInDays, startOfMonth, endOfMonth, addMonths } from 'date-fns';

// Brand colors - Gamer Packaging
const GAMER_RED = [179, 58, 43];  // #B33A2B - matching website
const DARK_GRAY = [51, 51, 51];
const LIGHT_GRAY = [245, 245, 245];
const HEADER_GRAY = [80, 80, 80];
const WHITE = [255, 255, 255];

// Status colors
const STATUS_GREEN = [34, 197, 94];
const STATUS_BLUE = [59, 130, 246];
const STATUS_GRAY = [156, 163, 175];
const STATUS_RED = [239, 68, 68];
const STATUS_AMBER = [245, 158, 11];

// Bar colors
const BAR_BACKGROUND = [229, 231, 235];
const BAR_BLUE = [96, 165, 250];
const BAR_GREEN = [34, 197, 94];

function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    return format(parseISO(dateStr), "M/d/yyyy");
  } catch {
    return "";
  }
}

function getMonthLabel(date) {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months[date.getMonth()];
}

export async function generateProjectReport(reportData, logoUrl) {
  const doc = new jsPDF('landscape', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  
  const project = reportData.project;
  const taskGroups = reportData.task_groups || [];
  
  // Load logo
  let logoImg = null;
  try {
    const response = await fetch(logoUrl);
    const blob = await response.blob();
    logoImg = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn("Could not load logo:", e);
  }

  let yPos = margin;

  // ============ HEADER SECTION ============
  // Logo on left (white background, no colored elements near it)
  if (logoImg) {
    // Increased height from 12 to 18 to fix squished appearance
    doc.addImage(logoImg, 'JPEG', margin, yPos, 40, 18);
  }
  
  // Project title - positioned to the right of logo
  const projectNumber = project.name || '';
  const customerName = project.customer || '';
  const projectTitle = `${projectNumber} - ${customerName}`;
  
  doc.setFontSize(16);
  doc.setTextColor(...DARK_GRAY);
  doc.setFont('helvetica', 'bold');
  doc.text(projectTitle, margin + 48, yPos + 8);
  
  if (project.description) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...HEADER_GRAY);
    doc.text(project.description.substring(0, 90), margin + 48, yPos + 14);
  }
  
  yPos += 22;
  
  // ============ PROJECT SUMMARY SECTION ============
  doc.setFillColor(...LIGHT_GRAY);
  doc.rect(margin, yPos, pageWidth - margin * 2, 22, 'F');
  
  doc.setFontSize(8);
  doc.setTextColor(...DARK_GRAY);
  
  // Column 1 - Dates
  const col1X = margin + 4;
  const col1ValX = margin + 40;
  
  doc.setFont('helvetica', 'bold');
  doc.text('Project Start Date:', col1X, yPos + 6);
  doc.setFont('helvetica', 'normal');
  doc.text(formatDate(project.start_date), col1ValX, yPos + 6);
  
  doc.setFont('helvetica', 'bold');
  doc.text('Project End Date:', col1X, yPos + 12);
  doc.setFont('helvetica', 'normal');
  doc.text(formatDate(project.end_date), col1ValX, yPos + 12);
  
  doc.setFont('helvetica', 'bold');
  doc.text('Duration:', col1X, yPos + 18);
  doc.setFont('helvetica', 'normal');
  doc.text(String(project.duration || 0) + ' days', col1ValX, yPos + 18);
  
  // Column 2 - Status
  const col2X = margin + 75;
  const col2ValX = margin + 110;
  
  doc.setFont('helvetica', 'bold');
  doc.text('Project Status:', col2X, yPos + 6);
  doc.setFont('helvetica', 'normal');
  doc.text(project.status || 'Unknown', col2ValX, yPos + 6);
  
  doc.setFont('helvetica', 'bold');
  doc.text('Schedule Status:', col2X, yPos + 12);
  const schedColor = project.schedule_status === 'Behind Schedule' ? STATUS_RED : STATUS_GREEN;
  doc.setTextColor(...schedColor);
  doc.setFont('helvetica', 'bold');
  doc.text(project.schedule_status || 'On Schedule', col2ValX, yPos + 12);
  doc.setTextColor(...DARK_GRAY);
  
  if (project.days_behind > 0) {
    doc.setFont('helvetica', 'bold');
    doc.text('Days Behind:', col2X, yPos + 18);
    doc.setTextColor(...STATUS_RED);
    doc.text(String(project.days_behind), col2ValX, yPos + 18);
    doc.setTextColor(...DARK_GRAY);
  }
  
  // Column 3 - Team
  const col3X = margin + 150;
  const col3ValX = margin + 165;
  
  doc.setFont('helvetica', 'bold');
  doc.text('OSR:', col3X, yPos + 6);
  doc.setFont('helvetica', 'normal');
  const osrText = ((project.osr || []).join(', ') || 'Unassigned').substring(0, 30);
  doc.text(osrText, col3ValX, yPos + 6);
  
  doc.setFont('helvetica', 'bold');
  doc.text('ISR:', col3X, yPos + 12);
  doc.setFont('helvetica', 'normal');
  const isrText = ((project.isr || []).join(', ') || 'Unassigned').substring(0, 30);
  doc.text(isrText, col3ValX, yPos + 12);
  
  // Progress bar (right side)
  const progressX = pageWidth - margin - 55;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text('Project Completion', progressX, yPos + 5);
  
  const barWidth = 50;
  const barHeight = 8;
  const barY = yPos + 7;
  
  // Bar background
  doc.setFillColor(...BAR_BACKGROUND);
  doc.roundedRect(progressX, barY, barWidth, barHeight, 2, 2, 'F');
  
  // Progress fill
  const progressPct = project.completion || 0;
  if (progressPct > 0) {
    const fillColor = progressPct >= 100 ? BAR_GREEN : BAR_BLUE;
    doc.setFillColor(...fillColor);
    doc.roundedRect(progressX, barY, Math.min((progressPct / 100) * barWidth, barWidth), barHeight, 2, 2, 'F');
  }
  
  // Progress text
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(progressPct > 50 ? 255 : 60, progressPct > 50 ? 255 : 60, progressPct > 50 ? 255 : 60);
  const pctText = progressPct.toFixed(1) + '%';
  doc.text(pctText, progressX + barWidth/2 - doc.getTextWidth(pctText)/2, barY + 6);
  
  doc.setTextColor(...DARK_GRAY);
  
  yPos += 25;
  
  // ============ PROJECT DETAILS TABLE ============
  doc.setFillColor(...GAMER_RED);
  doc.rect(margin, yPos, pageWidth - margin * 2, 6, 'F');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('Task Groups', margin + 3, yPos + 4.5);
  yPos += 6;
  
  // Table headers
  const tableHeaders = ['Task Group', 'Start Date', 'Due Date', 'Progress', 'Status'];
  const columnWidths = [95, 35, 35, 40, 52];
  const tableWidth = pageWidth - margin * 2;
  
  doc.setFillColor(...HEADER_GRAY);
  doc.rect(margin, yPos, tableWidth, 6, 'F');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  
  let tx = margin;
  tableHeaders.forEach((h, i) => {
    doc.text(h, tx + 3, yPos + 4.2);
    tx += columnWidths[i];
  });
  yPos += 6;
  
  // Table rows
  const rowHeight = 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  
  taskGroups.forEach((tg, idx) => {
    const rowBg = idx % 2 === 0 ? WHITE : [250, 250, 250];
    doc.setFillColor(...rowBg);
    doc.rect(margin, yPos, tableWidth, rowHeight, 'F');
    
    doc.setTextColor(...DARK_GRAY);
    tx = margin;
    
    // Task group name
    doc.setFont('helvetica', 'normal');
    doc.text((tg.name || 'Untitled').substring(0, 50), tx + 3, yPos + 4.2);
    tx += columnWidths[0];
    
    // Start date
    doc.text(formatDate(tg.start_date), tx + 3, yPos + 4.2);
    tx += columnWidths[1];
    
    // Due date
    doc.text(formatDate(tg.due_date), tx + 3, yPos + 4.2);
    tx += columnWidths[2];
    
    // Progress with mini bar
    const progress = tg.progress || 0;
    const miniBarW = 25;
    const miniBarH = 3.5;
    const miniBarY = yPos + 1.2;
    
    doc.setFillColor(...BAR_BACKGROUND);
    doc.roundedRect(tx + 2, miniBarY, miniBarW, miniBarH, 1, 1, 'F');
    
    if (progress > 0) {
      const fillColor = progress >= 100 ? BAR_GREEN : BAR_BLUE;
      doc.setFillColor(...fillColor);
      doc.roundedRect(tx + 2, miniBarY, (progress / 100) * miniBarW, miniBarH, 1, 1, 'F');
    }
    
    doc.setTextColor(...DARK_GRAY);
    doc.text(progress.toFixed(0) + '%', tx + miniBarW + 5, yPos + 4.2);
    tx += columnWidths[3];
    
    // Status badge
    const status = tg.status || 'Not Started';
    let statusColor = STATUS_GRAY;
    if (status === 'Complete') statusColor = STATUS_GREEN;
    else if (status === 'In Progress') statusColor = STATUS_BLUE;
    else if (status === 'Overdue') statusColor = STATUS_RED;
    
    doc.setFillColor(...statusColor);
    const badgeWidth = doc.getTextWidth(status) + 6;
    doc.roundedRect(tx + 2, yPos + 1, badgeWidth, 4, 1, 1, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(6);
    doc.text(status, tx + 5, yPos + 3.8);
    doc.setFontSize(7);
    
    yPos += rowHeight;
  });
  
  // Table border
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.rect(margin, yPos - (taskGroups.length * rowHeight) - 6, tableWidth, (taskGroups.length * rowHeight) + 6);
  
  yPos += 3;  // Reduced padding
  
  // ============ TIMELINE SECTION ============
  
  // Calculate date range
  let minDate = project.start_date ? new Date(project.start_date) : new Date();
  let maxDate = project.end_date ? new Date(project.end_date) : new Date();
  
  taskGroups.forEach(tg => {
    if (tg.start_date) {
      const d = new Date(tg.start_date);
      if (d < minDate) minDate = d;
    }
    if (tg.due_date) {
      const d = new Date(tg.due_date);
      if (d > maxDate) maxDate = d;
    }
  });
  
  minDate = startOfMonth(minDate);
  maxDate = endOfMonth(addMonths(maxDate, 1));
  const totalDays = Math.max(differenceInDays(maxDate, minDate), 1);
  
  // Get months
  const months = [];
  let currentMonth = new Date(minDate);
  while (currentMonth <= maxDate) {
    months.push(new Date(currentMonth));
    currentMonth = addMonths(currentMonth, 1);
  }
  
  // Calculate available space for Gantt
  const footerSpace = 8;
  const availableHeight = pageHeight - yPos - footerSpace;
  const ganttRowHeight = Math.min(7, Math.max(5, (availableHeight - 12) / taskGroups.length));
  
  // Timeline section header
  doc.setFillColor(...GAMER_RED);
  doc.rect(margin, yPos, pageWidth - margin * 2, 6, 'F');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('Timeline', margin + 3, yPos + 4.5);
  yPos += 6;
  
  // Layout
  const labelWidth = 50;
  const ganttX = margin + labelWidth;
  const ganttWidth = pageWidth - margin - ganttX;
  
  // Month headers
  const headerHeight = 7;
  
  doc.setFillColor(250, 250, 250);
  doc.rect(margin, yPos, labelWidth, headerHeight, 'F');
  doc.rect(ganttX, yPos, ganttWidth, headerHeight, 'F');
  
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK_GRAY);
  doc.text('Task Group', margin + 3, yPos + 4.5);
  
  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  
  months.forEach(m => {
    const startOffset = differenceInDays(m, minDate);
    const endOffset = differenceInDays(endOfMonth(m), minDate);
    
    const x1 = ganttX + (startOffset / totalDays) * ganttWidth;
    const x2 = ganttX + (endOffset / totalDays) * ganttWidth;
    const colWidth = x2 - x1;
    
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.1);
    doc.line(x1, yPos, x1, yPos + headerHeight);
    
    if (colWidth > 12) {
      const monthLabel = getMonthLabel(m) + ' ' + m.getFullYear();
      const centerX = x1 + colWidth / 2 - doc.getTextWidth(monthLabel) / 2;
      doc.setTextColor(...DARK_GRAY);
      doc.text(monthLabel, centerX, yPos + 4.5);
    }
  });
  
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.line(margin, yPos + headerHeight, pageWidth - margin, yPos + headerHeight);
  
  yPos += headerHeight;
  const barsStartY = yPos;
  
  // Task group rows
  doc.setFont('helvetica', 'normal');
  
  taskGroups.forEach((tg, idx) => {
    const rowBg = idx % 2 === 0 ? WHITE : [248, 250, 252];
    
    // Label cell
    doc.setFillColor(...rowBg);
    doc.rect(margin, yPos, labelWidth, ganttRowHeight, 'F');
    
    doc.setFontSize(6);
    doc.setTextColor(...DARK_GRAY);
    const tgName = (tg.name || 'Untitled').substring(0, 25);
    doc.text(tgName, margin + 2, yPos + ganttRowHeight/2 + 1.5);
    
    // Gantt cell
    doc.setFillColor(...rowBg);
    doc.rect(ganttX, yPos, ganttWidth, ganttRowHeight, 'F');
    
    // Vertical grid lines
    doc.setDrawColor(235, 235, 235);
    doc.setLineWidth(0.1);
    months.forEach(m => {
      const startOffset = differenceInDays(m, minDate);
      const x = ganttX + (startOffset / totalDays) * ganttWidth;
      doc.line(x, yPos, x, yPos + ganttRowHeight);
    });
    
    // Task group bar
    if (tg.start_date && tg.due_date) {
      const startDay = differenceInDays(new Date(tg.start_date), minDate);
      const endDay = differenceInDays(new Date(tg.due_date), minDate);
      
      const barX = ganttX + (startDay / totalDays) * ganttWidth;
      const barEndX = ganttX + (endDay / totalDays) * ganttWidth;
      const barW = Math.max(barEndX - barX, 10);
      const barH = ganttRowHeight - 2;
      
      const progress = tg.progress || 0;
      
      if (progress >= 100) {
        doc.setFillColor(...BAR_GREEN);
        doc.roundedRect(barX, yPos + 1, barW, barH, 1.5, 1.5, 'F');
      } else {
        doc.setFillColor(...BAR_BACKGROUND);
        doc.roundedRect(barX, yPos + 1, barW, barH, 1.5, 1.5, 'F');
        
        if (progress > 0) {
          doc.setFillColor(...BAR_BLUE);
          doc.roundedRect(barX, yPos + 1, (progress / 100) * barW, barH, 1.5, 1.5, 'F');
        }
      }
      
      // Progress label on bar
      if (barW > 15) {
        doc.setFontSize(5);
        doc.setTextColor(255, 255, 255);
        const pctLabel = Math.round(progress) + '%';
        doc.text(pctLabel, barX + barW/2 - doc.getTextWidth(pctLabel)/2, yPos + ganttRowHeight/2 + 1);
      }
    }
    
    yPos += ganttRowHeight;
  });
  
  const ganttEndY = yPos;
  
  // Borders
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.rect(margin, barsStartY - headerHeight, labelWidth, ganttEndY - (barsStartY - headerHeight));
  doc.rect(ganttX, barsStartY - headerHeight, ganttWidth, ganttEndY - (barsStartY - headerHeight));
  
  // ============ FOOTER ============
  doc.setFontSize(7);
  doc.setTextColor(...HEADER_GRAY);
  doc.setFont('helvetica', 'normal');
  doc.text('Report Generated: ' + format(new Date(), 'MMMM d, yyyy'), margin, pageHeight - 4);
  
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...GAMER_RED);
  doc.text('GAMER PACKAGING', pageWidth - margin - 35, pageHeight - 4);
  
  return doc;
}

export function downloadReport(doc, filename) {
  doc.save(filename);
}
